import { useEffect, useState } from "react";
import axios from "axios";
import { useHistory, useLocation } from "react-router-dom";
import { Alert, Button, Form, Input } from "antd";
import PageLayout from "pages/auth/page-layout";
import { ApiURL, AppURL } from "utils/Actions";
import api from "ApiClient";
import { Trans, useTranslation } from "react-i18next";
import { handleRedirect } from "utils/hooks/handleRedirect";

const commonErrorMessage = "Email not sent or token not updated.";
const defaultErrorMessage = "Something went wrong with your request";

export default function ForgetPassword() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [email, setEmail] = useState(false);
  const [error, setError] = useState<string>();
  const [isUnRegistered, setIsUnRegistered] = useState(false);

  useEffect(() => {
    handleRedirect(history, location.pathname);
  }, []);

  const handleOnFinish = async (values: any) => {
    setEmailSent(false);
    const { email } = values;
    setIsUnRegistered(false);
    setIsSubmitting(true);

    try {
      const sendResult = await api.patch(ApiURL.forgotPassword, {
        email: email
      });
      setEmail(email);
      if (!sendResult.data.data) {
        setIsUnRegistered(true);
        setEmailSent(true);
      } else {
        const { emailSent, tokenUpdated } = sendResult?.data?.data;
        if (emailSent && tokenUpdated) {
          setEmailSent(true);
        } else {
          setError(commonErrorMessage);
          throw new Error(commonErrorMessage);
        }
      }
    } catch (error) {
      setError("Something wrong occurred");
      console.error({ error });
      if (axios.isAxiosError(error)) {
        const errMsg = error.response?.data.message;
        setError(errMsg);
      } else {
        setError(defaultErrorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (emailSent) {
    return (
      <PageLayout isSingleCard={true}>
        <h1>{t("forgetPassword.isEmailSent.heading")}</h1>
        <div className="text-center">
          <Trans>{t("forgetPassword.isEmailSent.unregistered", { email: email })}</Trans>
        </div>
        <Button
          block
          type="link"
          onClick={() => {
            history.push(AppURL.login);
          }}
        >
          {t("forgetPassword.isEmailSent.CTA")}
        </Button>
      </PageLayout>
    );
  }

  return (
    <PageLayout isSingleCard={true}>
      <h1>{t("forgetPassword.heading")}</h1>
      <p className="text-center">{t("forgetPassword.description")}</p>
      {error && <Alert message={error} type="error" closable showIcon style={{ width: "100%" }} />}
      <Form
        layout="vertical"
        onFinish={handleOnFinish}
        requiredMark={false}
        style={{ width: "100%" }}
      >
        <Form.Item
          required
          label="Email"
          name="email"
          validateTrigger={"onBlur"}
          rules={[
            {
              required: true,
              message: "Email is required"
            },
            {
              required: false,
              type: "email",
              message: "Incorrect email format."
            }
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            block
            htmlType="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
            style={{ marginTop: 8 }}
          >
            {t("forgetPassword.submitCTA")}
          </Button>
        </Form.Item>
        <Button
          block
          type="link"
          onClick={() => {
            history.push(AppURL.login);
          }}
        >
          {t("forgetPassword.loginCTA")}
        </Button>
      </Form>
    </PageLayout>
  );
}
